import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';

import { Box, ToggleButton, useTheme } from '@mui/material';
import { Bold, Italic, Link, List, ListOrdered, Unlink } from 'lucide-react';
import type Quill from 'quill';

import {
  type ComposerToolbarState,
  LinkDialogContent,
  ToolbarDialog,
  applyComposerLink,
  readComposerToolbarState,
} from './ComposerLinkDialog';

const identity = (url: string): string => url;

const preventDefaultOnMouseDown = (event: MouseEvent) => event.preventDefault();

type ComposerToolbarProps = {
  quill: Quill | null;
  disabled: boolean;
  transformLinkForDisplay?: (url: string) => string;
  transformLinkForSave?: (url: string) => string;
  children?: ReactNode;
  actions?: ReactNode;
};

export const ComposerToolbar = ({
  quill,
  disabled,
  transformLinkForDisplay = identity,
  transformLinkForSave = identity,
  children,
  actions,
}: ComposerToolbarProps) => {
  const { palette } = useTheme();
  const [state, setState] = useState<ComposerToolbarState>({
    isBold: false,
    isItalic: false,
    blockType: 'paragraph',
    link: null,
    selectedText: '',
  });
  const [linkAnchor, setLinkAnchor] = useState<null | HTMLElement>(null);
  const [linkDialogKey, setLinkDialogKey] = useState(0);

  const { isBold, isItalic, blockType, link, selectedText } = state;

  useEffect(() => {
    if (quill == null || disabled) {
      return;
    }
    const update = () => {
      if (quill.getSelection() == null) {
        return;
      }
      setState(readComposerToolbarState(quill, transformLinkForDisplay));
    };
    quill.on('editor-change', update);
    update();

    return () => {
      quill.off('editor-change', update);
    };
  }, [quill, disabled, transformLinkForDisplay]);

  const toggleFormat = (name: 'bold' | 'italic') => {
    if (quill == null) {
      return;
    }
    const active = name === 'bold' ? isBold : isItalic;
    quill.format(name, !active, 'user');
    setState(prev =>
      name === 'bold'
        ? { ...prev, isBold: !active }
        : { ...prev, isItalic: !active },
    );
  };

  const toggleList = (type: 'ordered' | 'bullet') => {
    if (quill == null) {
      return;
    }
    quill.format('list', blockType === type ? false : type, 'user');
  };

  return (
    <Box
      sx={{
        overflowX: 'auto',
        borderBottom: `1px solid ${palette.divider}`,
        '.MuiToggleButton-root': { border: '0 !important' },
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="bold"
          selected={isBold}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={() => toggleFormat('bold')}
        >
          <Bold size={18} data-testid="bold-icon" />
        </ToggleButton>

        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="italic"
          selected={isItalic}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={() => toggleFormat('italic')}
        >
          <Italic size={18} data-testid="italic-icon" />
        </ToggleButton>

        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="ordered"
          selected={blockType === 'ordered'}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={() => toggleList('ordered')}
        >
          <ListOrdered size={18} data-testid="ordered-list-icon" />
        </ToggleButton>

        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="bullet"
          selected={blockType === 'bullet'}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={() => toggleList('bullet')}
        >
          <List size={18} data-testid="bullet-list-icon" />
        </ToggleButton>

        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="link"
          selected={link != null}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={event => {
            setLinkDialogKey(key => key + 1);
            setLinkAnchor(event.currentTarget);
          }}
        >
          <Link size={18} data-testid="link-icon" />
        </ToggleButton>

        <ToggleButton
          tabIndex={-1}
          disabled={disabled}
          value="unlink"
          selected={false}
          onMouseDown={preventDefaultOnMouseDown}
          onChange={() => {
            if (quill != null) {
              quill.format('link', false, 'user');
            }
          }}
        >
          <Unlink size={18} data-testid="unlink-icon" />
        </ToggleButton>

        {children}

        <Box sx={{ flexGrow: 1 }} />
        {actions}

        <ToolbarDialog
          anchorEl={linkAnchor}
          open={linkAnchor != null}
          onClose={() => setLinkAnchor(null)}
        >
          <LinkDialogContent
            key={linkDialogKey}
            link={link}
            title={selectedText}
            onSave={({ url, title }) => {
              setLinkAnchor(null);
              if (quill == null) {
                return;
              }
              applyComposerLink(quill, {
                url: transformLinkForSave(url),
                title,
              });
            }}
            onClose={() => setLinkAnchor(null)}
          />
        </ToolbarDialog>
      </Box>
    </Box>
  );
};
